import { useState } from 'react';
import { Button } from '../common/Button';
import { ConfirmModal } from '../common/Modal';
import {
  usePaperclipMembers,
  useRefreshPaperclipMembers,
  useSetPaperclipMemberAttribution,
  useSetPaperclipAttributionSwitch,
  useSetPaperclipTokenSyncSwitch,
  useSetPaperclipProvisioningSwitch,
  useProvisionPaperclipMembers,
  useSetPaperclipPersonAgentsSwitch,
  useReconcilePaperclipPersonAgents,
  useResyncPaperclipPersonAgents,
} from '../../hooks/useBotAdmin';
import { formatDisplayDateTime } from '../../utils/format';
import { RobotTokenSyncCard } from './RobotTokenSyncCard';
import { FactoryRepoPolicyCard } from './FactoryRepoPolicyCard';
import type {
  PaperclipMemberCompany,
  PaperclipMemberPerson,
  PaperclipMemberState,
  PaperclipPersonAgentLastResync,
  PaperclipPersonAgentStatus,
  PaperclipProvisionStatus,
} from '../../services/api/botAdmin';

/**
 * Phase 121 (Plan 121-03 / PCATTR-06, PCATTR-07, D-14, D-15, D-16) — the
 * "Factory™" tab on /admin/bot: the global per-person attribution switch, one
 * section per bound Factory™ company (last sync / request / failure and a
 * Refresh button) and a people table showing why each person is or is not
 * attributed, with a guarded Enable action.
 *
 * The server is the authority on readiness: Enable is only OFFERED for a
 * non-stale `ready_disabled` row, but a 409 from the backend guard is still shown
 * inside the confirm modal. This surface never calls Factory™ directly; Refresh only
 * enqueues the plugin read.
 *
 * Phase 124 (PCMEMB-09, PCMEMB-11, D-01, D-11, D-16) — on production (when
 * `provision.available`) the tab also drives account provisioning: a switch, a bulk
 * "Provision missing" action and a per-row "Provision" action. Provisioning creates a
 * verified, credential-less Factory™ account and an operator MONA membership through the
 * factory route, archives only memberships Monksheets created, and never invites or
 * notifies anyone. The server re-checks the company, the gate, the switch and the scope; the
 * UI only offers the actions it expects to be accepted.
 *
 * Phase 125 (PCAGT-13, PCAGT-10, PCAGT-09, D-13, D-17, D-18) — per-person builder agents:
 * an Agent column with the stored agent name and its state, the default-off switch, and the
 * read-only pilot list, a per-row Reconcile (re-run the pause/resume lifecycle) and a per-company
 * "Re-sync person agents" (refresh the clones from the shared builder). Both only enqueue jobs;
 * nothing here renames, creates or terminates an agent. Admin only and dark behind the switch,
 * so there is no release flag.
 */

const STATE_LABELS: Record<PaperclipMemberState, string> = {
  no_match: 'No match',
  not_member: 'Not a member',
  viewer: 'Viewer',
  ready_disabled: 'Ready (disabled)',
  enabled: 'Enabled',
};

const STATE_BADGE_CLASSES: Record<PaperclipMemberState, string> = {
  no_match: 'bg-gray-100 text-gray-600',
  not_member: 'bg-gray-100 text-gray-700',
  viewer: 'bg-amber-100 text-amber-700',
  ready_disabled: 'bg-blue-100 text-blue-700',
  enabled: 'bg-green-100 text-green-700',
};

function StateBadge({ state, stale }: { state: PaperclipMemberState; stale: boolean }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATE_BADGE_CLASSES[state]}`}
      >
        {STATE_LABELS[state]}
      </span>
      {stale && (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
          Stale
        </span>
      )}
    </span>
  );
}

/**
 * Phase 122 (D-15, D-16) — the per-person Claude-token badge. `synced` carries its
 * secret version; `self_managed` is a secret the person set themselves (never
 * touched by the writer); `deleted`, `failed` and `drift` mean the writer-managed
 * token is not in Factory™. A request in flight adds a "<op> pending" suffix.
 */
const TOKEN_BADGE_CLASSES: Record<string, string> = {
  synced: 'bg-green-100 text-green-700',
  self_managed: 'bg-blue-100 text-blue-700',
  deleted: 'bg-gray-100 text-gray-700',
  failed: 'bg-red-100 text-red-700',
  drift: 'bg-amber-100 text-amber-700',
};

function tokenLabel(person: PaperclipMemberPerson): string {
  let base: string;
  switch (person.tokenSyncStatus) {
    case 'synced':
      base = person.tokenSyncVersion !== null ? `Synced (v${person.tokenSyncVersion})` : 'Synced';
      break;
    case 'self_managed':
      base = 'Self-managed';
      break;
    case 'deleted':
      base = 'Deleted';
      break;
    case 'failed':
      base = person.tokenSyncErrorCode ? `Failed (${person.tokenSyncErrorCode})` : 'Failed';
      break;
    case 'drift':
      base = 'Drift';
      break;
    default:
      base = 'Not synced';
  }
  return person.tokenSyncPendingOperation ? `${base} · ${person.tokenSyncPendingOperation} pending` : base;
}

function TokenSyncBadge({ person }: { person: PaperclipMemberPerson }) {
  const classes =
    (person.tokenSyncStatus && TOKEN_BADGE_CLASSES[person.tokenSyncStatus]) ||
    'bg-gray-100 text-gray-600';
  return (
    <span
      title={`Last synced ${formatDisplayDateTime(person.tokenSyncedAt)}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}
    >
      {tokenLabel(person)}
    </span>
  );
}

/**
 * Phase 124 (D-11) — the per-person provisioning status. `refused` and `failed` carry the
 * server's fixed code (never free text); "by Monksheets" marks a membership Monksheets created.
 */
const PROVISION_BADGE_CLASSES: Record<PaperclipProvisionStatus, string> = {
  requested: 'bg-blue-100 text-blue-700',
  provisioned: 'bg-green-100 text-green-700',
  archived: 'bg-gray-100 text-gray-700',
  refused: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
};

function provisionLabel(person: PaperclipMemberPerson): string | null {
  switch (person.provisionStatus) {
    case 'requested':
      return 'Requested';
    case 'provisioned':
      return 'Provisioned';
    case 'archived':
      return 'Archived';
    case 'refused':
      return person.provisionErrorCode ? `Refused (${person.provisionErrorCode})` : 'Refused';
    case 'failed':
      return person.provisionErrorCode ? `Failed (${person.provisionErrorCode})` : 'Failed';
    default:
      return null;
  }
}

function ProvisionBadge({ person }: { person: PaperclipMemberPerson }) {
  const label = provisionLabel(person);
  if (label === null || person.provisionStatus === null) {
    return person.provisionedByMonksheets ? (
      <span className="text-xs text-gray-500">by Monksheets</span>
    ) : (
      <span className="text-gray-400">-</span>
    );
  }
  return (
    <span
      title={`Last provisioning event ${formatDisplayDateTime(person.provisionAt)}`}
      className="inline-flex items-center gap-1"
    >
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PROVISION_BADGE_CLASSES[person.provisionStatus]}`}
      >
        {label}
      </span>
      {person.provisionedByMonksheets && (
        <span className="text-xs text-gray-500">by Monksheets</span>
      )}
    </span>
  );
}

/**
 * Phase 125 (D-17) — the person's own builder agent. `busy` means a pause was requested but the
 * agent was working, so it stays active until the next reconcile; a request in flight shows as
 * Requested (the operation is in the tooltip); `failed` carries the server's fixed code.
 */
const AGENT_BADGE_CLASSES: Record<PaperclipPersonAgentStatus, string> = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-gray-100 text-gray-700',
  failed: 'bg-red-100 text-red-700',
  absent: 'bg-amber-100 text-amber-700',
};

function agentBadge(person: PaperclipMemberPerson): { label: string; classes: string } | null {
  if (person.personAgentPendingOperation) {
    return { label: 'Requested', classes: 'bg-blue-100 text-blue-700' };
  }
  const status = person.personAgentStatus;
  if (status === null) return null;
  if (status === 'active' && person.personAgentErrorCode === 'busy') {
    return { label: 'Pause pending (busy)', classes: 'bg-amber-100 text-amber-700' };
  }
  const base = { active: 'Active', paused: 'Paused', failed: 'Failed', absent: 'Absent' }[status];
  const label =
    (status === 'failed' || status === 'absent') && person.personAgentErrorCode
      ? `${base} (${person.personAgentErrorCode})`
      : base;
  return { label, classes: AGENT_BADGE_CLASSES[status] };
}

function AgentCell({ person }: { person: PaperclipMemberPerson }) {
  const badge = agentBadge(person);
  if (badge === null) return <span className="text-gray-400">-</span>;
  const title = person.personAgentPendingOperation
    ? `${person.personAgentPendingOperation} requested ${formatDisplayDateTime(person.personAgentRequestedAt)}`
    : `Last synced ${formatDisplayDateTime(person.personAgentSyncedAt)}`;
  return (
    <span title={title} className="inline-flex flex-col gap-0.5">
      <span
        className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.classes}`}
      >
        {badge.label}
      </span>
      {person.personAgentName && (
        <span className="text-xs text-gray-500">{person.personAgentName}</span>
      )}
    </span>
  );
}

/** One line for the last company re-sync: counts of the newest result, or its failure code. */
function lastResyncLabel(resync: PaperclipPersonAgentLastResync | undefined): string {
  if (!resync || (resync.requestedAt === null && resync.resultAt === null)) return '-';
  if (resync.resultAt === null) {
    return `requested ${formatDisplayDateTime(resync.requestedAt)}, no result yet`;
  }
  const when = formatDisplayDateTime(resync.resultAt);
  if (resync.checkedCount === null) {
    return `${when} — failed${resync.errorCode ? ` (${resync.errorCode})` : ''}`;
  }
  const parts = [
    `${resync.updatedCount ?? 0} of ${resync.checkedCount} updated`,
    `${resync.skippedBusyCount ?? 0} skipped busy`,
  ];
  if (resync.failedCount) parts.push(`${resync.failedCount} failed`);
  if (resync.errorCode) parts.push(`code ${resync.errorCode}`);
  return `${when} — ${parts.join(', ')}`;
}

function companyLabel(company: PaperclipMemberCompany): string {
  return company.projectName ?? `Project #${company.projectId}`;
}

/** What the provisioning confirm modal is about: everyone missing, or one person. */
type PendingProvision =
  | { kind: 'bulk'; companyName: string }
  | { kind: 'person'; person: PaperclipMemberPerson; companyName: string };

/** What the person-agent Reconcile confirm modal is about. */
interface PendingReconcile {
  person: PaperclipMemberPerson;
  companyName: string;
}

interface PendingRowAction {
  person: PaperclipMemberPerson;
  enable: boolean;
  companyName: string;
}

export function PaperclipMembersTab() {
  const { data, isLoading, error, refetch } = usePaperclipMembers();
  const switchMutation = useSetPaperclipAttributionSwitch();
  const tokenSyncMutation = useSetPaperclipTokenSyncSwitch();
  const provisioningSwitchMutation = useSetPaperclipProvisioningSwitch();
  const provisionMutation = useProvisionPaperclipMembers();
  const personAgentsSwitchMutation = useSetPaperclipPersonAgentsSwitch();
  const reconcileMutation = useReconcilePaperclipPersonAgents();
  const resyncMutation = useResyncPaperclipPersonAgents();
  const attributionMutation = useSetPaperclipMemberAttribution();
  const refreshMutation = useRefreshPaperclipMembers();

  const [pendingSwitch, setPendingSwitch] = useState<boolean | null>(null);
  const [pendingTokenSync, setPendingTokenSync] = useState<boolean | null>(null);
  const [pendingProvisioningSwitch, setPendingProvisioningSwitch] = useState<boolean | null>(null);
  const [pendingPersonAgentsSwitch, setPendingPersonAgentsSwitch] = useState<boolean | null>(null);
  const [pendingReconcile, setPendingReconcile] = useState<PendingReconcile | null>(null);
  const [reconcileNote, setReconcileNote] = useState<string | null>(null);
  const [pendingResync, setPendingResync] = useState<{
    companyId: string;
    companyName: string;
  } | null>(null);
  const [pendingProvision, setPendingProvision] = useState<PendingProvision | null>(null);
  const [pendingRow, setPendingRow] = useState<PendingRowAction | null>(null);
  const [showNoMatch, setShowNoMatch] = useState(false);

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm font-semibold text-red-600">Failed to load Factory™ members.</p>
        <p className="text-sm text-gray-500 mt-1">{(error as Error | null)?.message ?? ''}</p>
        <Button variant="secondary" size="sm" onClick={() => refetch()} className="mt-4">
          Try again
        </Button>
      </div>
    );
  }

  const switchOn = data.switchOn;
  const companyById = new Map(data.companies.map((c) => [c.paperclipCompanyId, c]));
  const noMatchCount = data.people.filter((p) => p.state === 'no_match').length;
  // A person with a provisioning status stays visible even with no mapping yet, so a request
  // an admin just made does not vanish from the table (Phase 124).
  const visiblePeople = showNoMatch
    ? data.people
    : data.people.filter((p) => p.state !== 'no_match' || p.provisionStatus !== null);

  const closeSwitchModal = () => {
    if (switchMutation.isPending) return;
    setPendingSwitch(null);
  };

  const confirmSwitch = () => {
    if (pendingSwitch === null) return;
    switchMutation.mutate(pendingSwitch, { onSuccess: () => setPendingSwitch(null) });
  };

  const closeTokenSyncModal = () => {
    if (tokenSyncMutation.isPending) return;
    setPendingTokenSync(null);
  };

  const confirmTokenSync = () => {
    if (pendingTokenSync === null) return;
    tokenSyncMutation.mutate(pendingTokenSync, { onSuccess: () => setPendingTokenSync(null) });
  };

  const closeProvisioningSwitchModal = () => {
    if (provisioningSwitchMutation.isPending) return;
    setPendingProvisioningSwitch(null);
  };

  const confirmProvisioningSwitch = () => {
    if (pendingProvisioningSwitch === null) return;
    provisioningSwitchMutation.mutate(pendingProvisioningSwitch, {
      onSuccess: () => setPendingProvisioningSwitch(null),
    });
  };

  const closePersonAgentsSwitchModal = () => {
    if (personAgentsSwitchMutation.isPending) return;
    setPendingPersonAgentsSwitch(null);
  };

  const confirmPersonAgentsSwitch = () => {
    if (pendingPersonAgentsSwitch === null) return;
    personAgentsSwitchMutation.mutate(pendingPersonAgentsSwitch, {
      onSuccess: () => setPendingPersonAgentsSwitch(null),
    });
  };

  const closeReconcileModal = () => {
    if (reconcileMutation.isPending) return;
    setPendingReconcile(null);
  };

  const confirmReconcile = () => {
    if (pendingReconcile === null) return;
    const { person } = pendingReconcile;
    reconcileMutation.mutate(
      { paperclipCompanyId: person.paperclipCompanyId, userId: person.userId },
      {
        onSuccess: (result) => {
          setReconcileNote(
            `Reconcile for ${person.displayName}: ${result.requested} requested, ${result.held} held, ${result.skipped} skipped.`
          );
          setPendingReconcile(null);
        },
      }
    );
  };

  const closeResyncModal = () => {
    if (resyncMutation.isPending) return;
    setPendingResync(null);
  };

  const confirmResync = () => {
    if (pendingResync === null) return;
    resyncMutation.mutate(pendingResync.companyId, { onSuccess: () => setPendingResync(null) });
  };

  const closeProvisionModal = () => {
    if (provisionMutation.isPending) return;
    setPendingProvision(null);
  };

  const confirmProvision = () => {
    if (pendingProvision === null || provision.companyId === null) return;
    provisionMutation.mutate(
      {
        paperclipCompanyId: provision.companyId,
        ...(pendingProvision.kind === 'person' ? { userId: pendingProvision.person.userId } : {}),
      },
      { onSuccess: () => setPendingProvision(null) }
    );
  };

  const closeRowModal = () => {
    if (attributionMutation.isPending) return;
    setPendingRow(null);
  };

  const confirmRowAction = () => {
    if (!pendingRow || pendingRow.person.mappingId === null) return;
    attributionMutation.mutate(
      { mappingId: pendingRow.person.mappingId, attributionEnabled: pendingRow.enable },
      { onSuccess: () => setPendingRow(null) }
    );
  };

  const turningSwitchOn = pendingSwitch === true;
  const tokenSync = data.tokenSync;
  const showTokenSync = tokenSync.available;
  const turningTokenSyncOn = pendingTokenSync === true;
  const provision = data.provision;
  const showProvision = provision.available;
  const turningProvisioningOn = pendingProvisioningSwitch === true;
  const personAgents = data.personAgents;
  const turningPersonAgentsOn = pendingPersonAgentsSwitch === true;
  const lastResyncByCompany = new Map(personAgents.lastResync.map((r) => [r.companyId, r]));
  const peopleNameById = new Map(data.people.map((p) => [p.userId, p.displayName]));
  const pilotNames = personAgents.pilotUserIds.map(
    (id) => peopleNameById.get(id) ?? `User #${id}`
  );
  const canProvisionMissing = provision.switchOn && provision.missing > 0;
  const employeeTypeSplit =
    provision.byEmployeeType.map((t) => `${t.name} ${t.count}`).join(', ') || 'none';
  // Per-row Provision: only for in-scope people of the provisioning company who have no
  // membership, or one Monksheets created that is no longer active (Phase 124, D-07, D-08).
  const canProvisionPerson = (person: PaperclipMemberPerson): boolean =>
    provision.switchOn &&
    provision.companyId !== null &&
    person.paperclipCompanyId === provision.companyId &&
    person.inProvisionScope &&
    (person.state === 'no_match' || (person.provisionedByMonksheets && person.state === 'not_member'));

  return (
    <div className="space-y-6">
      {/* (a) The global switch. */}
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Per-person attribution</p>
            <p className="text-sm mt-0.5">
              <span className={switchOn ? 'text-green-600 font-medium' : 'text-gray-600 font-medium'}>
                {switchOn ? 'Per-person attribution: ON' : 'Per-person attribution: OFF'}
              </span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              When ON, Factory™ runs are attributed to the person who requested the ticket, for
              people enabled below only. Everyone else keeps today&apos;s behaviour.
            </p>
          </div>
          <Button
            type="button"
            variant={switchOn ? 'secondary' : 'primary'}
            onClick={() => setPendingSwitch(!switchOn)}
          >
            {switchOn ? 'Turn off' : 'Turn on'}
          </Button>
        </div>
      </div>

      {/* (a2) Phase 122 — the Claude token sync switch (production writer only). */}
      {showTokenSync && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Claude token sync</p>
              <p className="text-sm mt-0.5">
                <span
                  className={
                    tokenSync.switchOn ? 'text-green-600 font-medium' : 'text-gray-600 font-medium'
                  }
                >
                  {tokenSync.switchOn ? 'Claude token sync: ON' : 'Claude token sync: OFF'}
                </span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                When ON, Monksheets copies each eligible person&apos;s Claude token into their
                Factory™ CLAUDE_SUBSCRIPTION_TOKEN secret and removes copies it created when they
                disconnect or become ineligible. Secrets people set themselves are never touched.
              </p>
            </div>
            <Button
              type="button"
              variant={tokenSync.switchOn ? 'secondary' : 'primary'}
              aria-label={
                tokenSync.switchOn ? 'Turn off Claude token sync' : 'Turn on Claude token sync'
              }
              onClick={() => {
                tokenSyncMutation.reset();
                setPendingTokenSync(!tokenSync.switchOn);
              }}
            >
              {tokenSync.switchOn ? 'Turn off' : 'Turn on'}
            </Button>
          </div>
        </div>
      )}

      {/* (a2b) MKS-275 — the TopMonks Robot's token, per bound company. The card
          hides itself when this environment names no company. */}
      <RobotTokenSyncCard robotTokenSync={data.robotTokenSync} />

      {/* (a2c) MKS-471 — the per-repository Factory policy editor. A global card (not per
          company). */}
      <FactoryRepoPolicyCard />

      {/* (a3) Phase 124 — the account provisioning switch (production only). */}
      {showProvision && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Account provisioning</p>
              <p className="text-sm mt-0.5">
                <span
                  className={
                    provision.switchOn ? 'text-green-600 font-medium' : 'text-gray-600 font-medium'
                  }
                >
                  {provision.switchOn
                    ? 'Account provisioning: ON'
                    : 'Account provisioning: OFF'}
                </span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                When ON, Monksheets asks the factory to create a verified account with no password
                for each in-scope colleague and add them to the Factory™ company as operator. Nobody
                is invited or notified. Departures archive only memberships Monksheets created.
                {' '}
                {provision.inScope} in scope, {provision.missing} missing.
              </p>
            </div>
            <Button
              type="button"
              variant={provision.switchOn ? 'secondary' : 'primary'}
              aria-label={
                provision.switchOn
                  ? 'Turn off account provisioning'
                  : 'Turn on account provisioning'
              }
              onClick={() => {
                provisioningSwitchMutation.reset();
                setPendingProvisioningSwitch(!provision.switchOn);
              }}
            >
              {provision.switchOn ? 'Turn off' : 'Turn on'}
            </Button>
          </div>
        </div>
      )}

      {/* (a4) Phase 125 — the per-person builder agent switch (admin only, default off). */}
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Per-person builder agents</p>
            <p className="text-sm mt-0.5">
              <span
                className={
                  personAgents.switchOn ? 'text-green-600 font-medium' : 'text-gray-600 font-medium'
                }
              >
                {personAgents.switchOn
                  ? 'Per-person builder agents: ON'
                  : 'Per-person builder agents: OFF'}
              </span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              When ON, a person&apos;s own builder agent is created on their first attributed Robot
              ticket, cloned from the company&apos;s shared builder, and paused when the person is
              archived. Everything unattributed keeps using the shared builder.
            </p>
            {personAgents.pilotUserIds.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                Pilot: {personAgents.pilotUserIds.length}{' '}
                {personAgents.pilotUserIds.length === 1 ? 'person' : 'people'} (
                {pilotNames.join(', ')})
              </p>
            )}
          </div>
          <Button
            type="button"
            variant={personAgents.switchOn ? 'secondary' : 'primary'}
            aria-label={
              personAgents.switchOn
                ? 'Turn off per-person builder agents'
                : 'Turn on per-person builder agents'
            }
            onClick={() => {
              personAgentsSwitchMutation.reset();
              setPendingPersonAgentsSwitch(!personAgents.switchOn);
            }}
          >
            {personAgents.switchOn ? 'Turn off' : 'Turn on'}
          </Button>
        </div>
      </div>

      {/* (b) One section per bound company. */}
      <div className="space-y-3">
        {data.companies.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-center">
            <p className="text-sm text-gray-500">
              No project is bound to a Factory™ company yet. Bind one on the Connections tab.
            </p>
          </div>
        )}
        {data.companies.map((company) => {
          const refreshedHere =
            refreshMutation.isSuccess && refreshMutation.variables === company.paperclipCompanyId;
          const refreshFailedHere =
            refreshMutation.isError && refreshMutation.variables === company.paperclipCompanyId;
          const refreshingHere =
            refreshMutation.isPending && refreshMutation.variables === company.paperclipCompanyId;
          return (
            <section
              key={company.paperclipCompanyId}
              aria-label={`Factory™ company ${company.paperclipCompanyId}`}
              className="rounded-lg border border-gray-200 bg-white px-4 py-3"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{companyLabel(company)}</p>
                  <p className="text-xs text-gray-400">Factory™ company {company.paperclipCompanyId}</p>
                  <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-gray-600 sm:grid-cols-3">
                    <div>
                      <dt className="inline text-gray-500">Last sync: </dt>
                      <dd className="inline">{formatDisplayDateTime(company.lastSyncedAt)}</dd>
                    </div>
                    <div>
                      <dt className="inline text-gray-500">Last request: </dt>
                      <dd className="inline">{formatDisplayDateTime(company.lastRequestedAt)}</dd>
                    </div>
                    <div>
                      <dt className="inline text-gray-500">Last failure: </dt>
                      <dd className="inline">
                        {company.lastFailedAt
                          ? `${formatDisplayDateTime(company.lastFailedAt)}${
                              company.lastFailureReason ? ` — ${company.lastFailureReason}` : ''
                            }`
                          : '-'}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-2 text-xs text-gray-500">
                    Rows not synced within {data.staleAfterHours} h do not attribute.
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    <span className="text-gray-500">Last agent re-sync: </span>
                    {lastResyncLabel(lastResyncByCompany.get(company.paperclipCompanyId))}
                  </p>
                  {resyncMutation.isSuccess &&
                    resyncMutation.variables === company.paperclipCompanyId && (
                      <p className="mt-1 text-xs text-green-700">
                        Agent re-sync requested. The result arrives asynchronously; reload in a
                        minute to see it.
                      </p>
                    )}
                  {refreshedHere && (
                    <p className="mt-2 text-xs text-green-700">
                      Refresh requested. The roster arrives asynchronously; reload in a minute to
                      see it.
                    </p>
                  )}
                  {refreshFailedHere && (
                    <p className="mt-2 text-xs text-red-600">
                      {(refreshMutation.error as Error | null)?.message ?? 'Refresh failed.'}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    isLoading={refreshingHere}
                    onClick={() => refreshMutation.mutate(company.paperclipCompanyId)}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={!personAgents.switchOn}
                    title={
                      personAgents.switchOn
                        ? undefined
                        : 'Turn per-person builder agents on first.'
                    }
                    onClick={() => {
                      resyncMutation.reset();
                      setPendingResync({
                        companyId: company.paperclipCompanyId,
                        companyName: companyLabel(company),
                      });
                    }}
                  >
                    Re-sync person agents
                  </Button>
                  {showProvision && company.paperclipCompanyId === provision.companyId && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canProvisionMissing}
                      title={
                        !provision.switchOn
                          ? 'Turn account provisioning on first.'
                          : provision.missing === 0
                            ? 'Nobody in scope is missing.'
                            : undefined
                      }
                      onClick={() => {
                        provisionMutation.reset();
                        setPendingProvision({ kind: 'bulk', companyName: companyLabel(company) });
                      }}
                    >
                      Provision missing ({provision.missing})
                    </Button>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* (c) The people table. */}
      {data.companies.length > 0 && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showNoMatch}
              onChange={(e) => setShowNoMatch(e.target.checked)}
            />
            Show people with no Factory™ match ({noMatchCount})
          </label>

          {reconcileNote && <p className="text-xs text-green-700">{reconcileNote}</p>}

          {visiblePeople.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-center">
              <p className="text-sm text-gray-500">No people are matched to a Factory™ member yet.</p>
            </div>
          ) : (
            <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      'Person',
                      'Email',
                      'Company',
                      'Factory™ role',
                      'State',
                      ...(showTokenSync ? ['Claude token'] : []),
                      ...(showProvision ? ['Provisioning'] : []),
                      'Agent',
                      'Synced',
                      '',
                    ].map((heading, i) => (
                      <th
                        key={`${heading}-${i}`}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visiblePeople.map((person, idx) => {
                    const company = companyById.get(person.paperclipCompanyId);
                    const companyName = company
                      ? companyLabel(company)
                      : person.paperclipCompanyId;
                    const canEnable = person.state === 'ready_disabled' && !person.stale;
                    return (
                      <tr
                        key={`${person.paperclipCompanyId}:${person.userId}`}
                        className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
                      >
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {person.displayName}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                          {person.email}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                          {companyName}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                          {person.membershipRole ?? '-'}
                        </td>
                        <td className="px-4 py-2 text-sm whitespace-nowrap">
                          <StateBadge state={person.state} stale={person.stale} />
                        </td>
                        {showTokenSync && (
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            <TokenSyncBadge person={person} />
                          </td>
                        )}
                        {showProvision && (
                          <td className="px-4 py-2 text-sm whitespace-nowrap">
                            {person.paperclipCompanyId === provision.companyId ? (
                              <ProvisionBadge person={person} />
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-2 text-sm whitespace-nowrap">
                          <AgentCell person={person} />
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                          {formatDisplayDateTime(person.syncedAt)}
                        </td>
                        <td className="px-4 py-2 text-right text-sm whitespace-nowrap">
                          {person.personAgentId !== null && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              aria-label={`Reconcile agent of ${person.displayName}`}
                              disabled={
                                person.personAgentPendingOperation !== null ||
                                !personAgents.switchOn
                              }
                              title={
                                person.personAgentPendingOperation !== null
                                  ? 'A request is already in flight.'
                                  : !personAgents.switchOn
                                    ? 'Turn per-person builder agents on first.'
                                    : undefined
                              }
                              onClick={() => {
                                reconcileMutation.reset();
                                setPendingReconcile({ person, companyName });
                              }}
                            >
                              Reconcile
                            </Button>
                          )}
                          {showProvision && canProvisionPerson(person) && (
                            <Button
                              type="button"
                              size="sm"
                              aria-label={`Provision ${person.displayName}`}
                              onClick={() => {
                                provisionMutation.reset();
                                setPendingProvision({ kind: 'person', person, companyName });
                              }}
                            >
                              Provision
                            </Button>
                          )}
                          {person.attributionEnabled ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              aria-label={`Disable attribution for ${person.displayName}`}
                              onClick={() => {
                                attributionMutation.reset();
                                setPendingRow({ person, enable: false, companyName });
                              }}
                            >
                              Disable
                            </Button>
                          ) : person.state === 'ready_disabled' ? (
                            <Button
                              type="button"
                              size="sm"
                              aria-label={`Enable attribution for ${person.displayName}`}
                              disabled={!canEnable}
                              title={
                                canEnable
                                  ? undefined
                                  : 'The roster is stale. Refresh the company first.'
                              }
                              onClick={() => {
                                attributionMutation.reset();
                                setPendingRow({ person, enable: true, companyName });
                              }}
                            >
                              Enable
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={pendingSwitch !== null}
        onClose={closeSwitchModal}
        onConfirm={confirmSwitch}
        title={
          turningSwitchOn
            ? 'Turn ON per-person attribution?'
            : 'Turn OFF per-person attribution?'
        }
        message={
          turningSwitchOn
            ? 'Factory™ runs will be attributed to the person who requested the ticket, for people enabled in the table below only. Everyone else is unchanged.'
            : "Every Factory™ run reverts to today's behaviour (no per-person attribution) within one dispatch. Individual opt-ins are kept."
        }
        confirmText={turningSwitchOn ? 'Turn on' : 'Turn off'}
        cancelText="Cancel"
        variant={turningSwitchOn ? 'primary' : 'danger'}
        isLoading={switchMutation.isPending}
        error={switchMutation.isError ? (switchMutation.error as Error).message : null}
      />

      <ConfirmModal
        isOpen={pendingTokenSync !== null}
        onClose={closeTokenSyncModal}
        onConfirm={confirmTokenSync}
        title={turningTokenSyncOn ? 'Turn ON Claude token sync?' : 'Turn OFF Claude token sync?'}
        message={
          turningTokenSyncOn
            ? "Monksheets will copy each eligible person's Claude token into their Factory™ CLAUDE_SUBSCRIPTION_TOKEN secret, and keep it in step. Secrets people set themselves are never touched."
            : 'Monksheets stops copying and verifying Claude tokens in Factory™. Secrets already copied stay where they are until you turn sync back on.'
        }
        confirmText={turningTokenSyncOn ? 'Turn on' : 'Turn off'}
        cancelText="Cancel"
        variant={turningTokenSyncOn ? 'primary' : 'danger'}
        isLoading={tokenSyncMutation.isPending}
        error={tokenSyncMutation.isError ? (tokenSyncMutation.error as Error).message : null}
      />

      <ConfirmModal
        isOpen={pendingProvisioningSwitch !== null}
        onClose={closeProvisioningSwitchModal}
        onConfirm={confirmProvisioningSwitch}
        title={
          turningProvisioningOn
            ? 'Turn ON account provisioning?'
            : 'Turn OFF account provisioning?'
        }
        message={
          turningProvisioningOn
            ? 'Monksheets will ask the factory to create a verified account with no password for each in-scope colleague and add them to the Factory™ company as operator, when you press Provision or at the daily run (04:40 UTC). Nobody is invited or notified. When someone leaves, only memberships Monksheets created are archived.'
            : 'Monksheets stops requesting and archiving Factory™ accounts. Accounts already created stay where they are until you turn provisioning back on.'
        }
        confirmText={turningProvisioningOn ? 'Turn on' : 'Turn off'}
        cancelText="Cancel"
        variant={turningProvisioningOn ? 'primary' : 'danger'}
        isLoading={provisioningSwitchMutation.isPending}
        error={
          provisioningSwitchMutation.isError
            ? (provisioningSwitchMutation.error as Error).message
            : null
        }
      />

      <ConfirmModal
        isOpen={pendingPersonAgentsSwitch !== null}
        onClose={closePersonAgentsSwitchModal}
        onConfirm={confirmPersonAgentsSwitch}
        title={
          turningPersonAgentsOn
            ? 'Turn ON per-person builder agents?'
            : 'Turn OFF per-person builder agents?'
        }
        message={
          turningPersonAgentsOn
            ? "Each person's Robot tickets will run on their own builder agent, created on their first attributed ticket by cloning the shared builder (the pilot list, if any, limits who). Archived people's agents are paused. Everything unattributed still uses the shared builder."
            : 'Every Robot ticket goes back to the shared builder from the next dispatch. Agents already created are kept as they are.'
        }
        confirmText={turningPersonAgentsOn ? 'Turn on' : 'Turn off'}
        cancelText="Cancel"
        variant={turningPersonAgentsOn ? 'primary' : 'danger'}
        isLoading={personAgentsSwitchMutation.isPending}
        error={
          personAgentsSwitchMutation.isError
            ? (personAgentsSwitchMutation.error as Error).message
            : null
        }
      />

      <ConfirmModal
        isOpen={pendingReconcile !== null}
        onClose={closeReconcileModal}
        onConfirm={confirmReconcile}
        title={`Reconcile the agent of ${pendingReconcile?.person.displayName ?? ''}?`}
        message={`Monksheets compares the agent of ${pendingReconcile?.person.displayName ?? ''} in ${pendingReconcile?.companyName ?? ''} with their account and pauses it if they are archived, or resumes it if they are back. Nothing is created, renamed or removed; a busy agent keeps working and is paused on the next daily run.`}
        confirmText="Reconcile"
        cancelText="Cancel"
        variant="primary"
        isLoading={reconcileMutation.isPending}
        error={reconcileMutation.isError ? (reconcileMutation.error as Error).message : null}
      />

      <ConfirmModal
        isOpen={pendingResync !== null}
        onClose={closeResyncModal}
        onConfirm={confirmResync}
        title="Re-sync person agents?"
        message={`Every person's builder agent in ${pendingResync?.companyName ?? ''} is refreshed from the shared builder's current bundle and configuration. Agents that are working are skipped and picked up on the next run. Names and owners do not change.`}
        confirmText="Re-sync"
        cancelText="Cancel"
        variant="primary"
        isLoading={resyncMutation.isPending}
        error={resyncMutation.isError ? (resyncMutation.error as Error).message : null}
      />

      <ConfirmModal
        isOpen={pendingProvision !== null}
        onClose={closeProvisionModal}
        onConfirm={confirmProvision}
        title={
          pendingProvision?.kind === 'person'
            ? `Provision ${pendingProvision.person.displayName}?`
            : 'Provision missing accounts?'
        }
        message={
          pendingProvision?.kind === 'person'
            ? `Monksheets will ask the factory to create a verified account with no password for ${pendingProvision.person.displayName} and add them to ${pendingProvision.companyName} as operator. Nobody is invited or notified.`
            : `Provision ${provision.missing} of ${provision.inScope} in-scope people in ${pendingProvision?.companyName ?? ''}?\n\nIn-scope people by employee type: ${employeeTypeSplit}.\n\nEach gets a verified account with no password and an operator membership. Nobody is invited or notified.`
        }
        confirmText={pendingProvision?.kind === 'person' ? 'Provision' : 'Provision missing'}
        cancelText="Cancel"
        variant="primary"
        isLoading={provisionMutation.isPending}
        error={provisionMutation.isError ? (provisionMutation.error as Error).message : null}
      />

      <ConfirmModal
        isOpen={pendingRow !== null}
        onClose={closeRowModal}
        onConfirm={confirmRowAction}
        title={
          pendingRow?.enable
            ? `Enable attribution for ${pendingRow.person.displayName}?`
            : `Disable attribution for ${pendingRow?.person.displayName ?? ''}?`
        }
        message={
          pendingRow?.enable
            ? `Factory™ runs requested by ${pendingRow.person.displayName} in ${pendingRow.companyName} will be attributed to them. Their CLAUDE_SUBSCRIPTION_TOKEN must already exist in that Factory™ company before you enable this.`
            : `Runs requested by ${pendingRow?.person.displayName ?? ''} in ${pendingRow?.companyName ?? ''} go back to today's behaviour (not attributed to them) from the next dispatch.`
        }
        confirmText={pendingRow?.enable ? 'Enable' : 'Disable'}
        cancelText="Cancel"
        variant={pendingRow?.enable ? 'primary' : 'danger'}
        isLoading={attributionMutation.isPending}
        error={
          attributionMutation.isError ? (attributionMutation.error as Error).message : null
        }
      />
    </div>
  );
}
